/**
 * TOSS Web Application — Client Portal: applying a company switch
 *
 * Two steps, in a FIXED order, shared by the two triggers that both have to leave the
 * portal in an identical state afterwards (client-portal Р4, decision Р20 — the header
 * switcher):
 *
 *   1. `discardForeignRouteOnCompanySwitch()` — synchronous, first, always.
 *   2. `applyCompanySwitch()` — async: cache, `authStore`, the messenger socket.
 *
 * Both triggers call them in that order:
 *  - `useSwitchCompany` (`useClientProfile.ts`) — the dropdown's own click. Its mutation
 *    response body already IS the fresh `ClientProfileDto`, so it hands that straight in.
 *  - `useClientSessionRealtime` — the `CompanySwitched` SignalR push every OTHER open tab
 *    of the account receives (and this tab too, for a switch an administrator forced by
 *    revoking the active company — `CustomerUsersService`, not this session, decided it).
 *    That push carries only a bare `customerId`, so step 2 fetches the profile itself.
 *
 * Kept out of both hook files, in `hooks/` rather than `features/client/**`, for the same
 * reason `clientQueryKeys.ts` is: the client-portal ESLint boundary (decision Р6) makes
 * `features/client/**` unimportable from `components/layout/**`, and both triggers above
 * are layout chrome or chrome-adjacent.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction } from 'react-router-dom';
import { clientCompanyApi, type ClientProfileDto } from '@/api/clientPortal/clientCompany.api';
import { restartClientMessengerHub } from './useClientChatRealtime';
import { clientKeys } from './clientQueryKeys';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/constants/routes';
import { FORCED_LEAVE_STATE } from '@/hooks/useUnsavedChangesGuard';

// ============================================================================
// Which routes must not survive a switch
// ============================================================================

/**
 * Portal route prefixes that hold data or a draft scoped to the company active WHEN THEY
 * WERE OPENED, not to whichever company is active now.
 *
 *  - `/client/applications/…` covers both a detail card (`/client/applications/:id`, which
 *    would 404 under the new company — company-switch contract §6) AND the create form
 *    (`/client/applications/new`, blocker B4 of r5-contract-decisions.md §1: a prefilled
 *    draft — copied or from a template — must not go on to be submitted with company A's
 *    shipper/consignee/wagons under company B). One prefix covers both because neither may
 *    survive and the create route is a child path of the same feature root.
 *  - `/client/containers/…` covers the container card the same way.
 *
 *  - `/client/templates/…` covers the template FORM (`/client/templates/new` and
 *    `/client/templates/:id/edit`). Same blocker B4, same shape: a route, a react-hook-form
 *    draft, and one click between it and company B's library.
 *
 * List pages are deliberately NOT here: `/client/applications`, `/client/containers`,
 * `/client/documents`, `/client/messenger` etc. name no id and hold no draft — their
 * queries are already gone from the cache-wide `removeQueries` below, so staying in place
 * and refetching scoped to the new company is the correct behaviour, not a hazard.
 * The one list route that DOES hold a draft — `/client/templates`, whose rename dialog keeps a
 * react-hook-form state — is matched exactly, in the second list below.
 */
const ROUTE_PREFIXES_TO_ABANDON_ON_SWITCH: readonly string[] = [
  `${ROUTES.client.applications.list}/`,
  `${ROUTES.client.containers.list}/`,
  // A chat id belongs to ONE company. Without this, switching legal entity leaves the previous
  // company's conversation on screen until something else happens to navigate — and the first
  // thing the customer would type goes into a chat they are no longer acting for. The list itself
  // (`/client/messenger`, no id) survives the switch and simply reloads.
  `${ROUTES.client.messenger}/`,
  // The template form. Until 27.08.2026 it was a MODAL on the list route below, and the exact
  // match there was the whole protection; then the form moved onto `/client/templates/new` and
  // `/client/templates/:id/edit` and the rule stayed behind. For a day the docblock under that
  // exact match described this leak as prevented while it was live: a route matched by equality
  // does not match its own children, so a shipper, consignee and forty wagon numbers typed for
  // company A stayed on screen through the switch and went into company B's library on the next
  // click. A prefix cannot fall behind a route the same way.
  `${ROUTES.client.templates}/`,
];

/**
 * Routes with no id segment that still hold a DRAFT and therefore cannot survive a switch —
 * matched exactly, not by prefix. `/client/templates` still qualifies after the form moved off
 * it: the RENAME dialog keeps a react-hook-form state that `removeQueries` does not touch, and a
 * name typed for company A must not be saved into company B's library.
 *
 * The form itself is covered by the `/client/templates/` PREFIX above, not here — an exact match
 * does not match child routes, and that gap is exactly how the leak lived.
 */
const ROUTES_TO_ABANDON_ON_SWITCH_EXACT: readonly string[] = [ROUTES.client.templates];

/**
 * Whether the current route must be abandoned because it names, or was built from, data
 * belonging to the company the session no longer acts for.
 */
export function routeNeedsDiscardOnCompanySwitch(pathname: string): boolean {
  return (
    ROUTE_PREFIXES_TO_ABANDON_ON_SWITCH.some((prefix) => pathname.startsWith(prefix)) ||
    ROUTES_TO_ABANDON_ON_SWITCH_EXACT.includes(pathname)
  );
}

/**
 * Navigates away from a route that would otherwise render the WRONG company's data or
 * 404 — a **synchronous, no-op-safe** call that both `CompanySwitched` triggers must make
 * BEFORE calling {@link applyCompanySwitch}, never after.
 *
 * The ordering is load-bearing, not stylistic. `applyCompanySwitch` starts with
 * `queryClient.removeQueries(...)`, which immediately flips every still-mounted portal
 * query (an application card's `useClientApplication(id)` included) into a refetch —
 * and that refetch race-answers 404 under the new company, since the switch already
 * committed server-side before this event ever fired. Calling THIS first means React
 * Router already has a pending navigation to the dashboard queued before that cache
 * churn starts, so the doomed detail page never gets a chance to render its own 404. Swap
 * the two calls and a customer would see exactly the "404" flash both this function and
 * the company-switch contract's §6 exist to prevent.
 */
export function discardForeignRouteOnCompanySwitch(
  navigate: NavigateFunction,
  pathname: string,
  search = ''
): void {
  if (routeNeedsDiscardOnCompanySwitch(pathname)) {
    // Marked forced: a form on the abandoned screen would otherwise hold this navigation and
    // ask whether to discard a draft that belongs to the company we have just left. There is
    // no question to ask — see `FORCED_LEAVE_STATE`.
    navigate(ROUTES.client.dashboard, { replace: true, state: FORCED_LEAVE_STATE });
    return;
  }

  // The route stays, its QUERY STRING does not. `usePersistentTableFilters` mirrors search
  // text, column filters and sort into the URL as well as into storage, and a list page is
  // deliberately not abandoned on a switch — so without this the customer stays on
  // `/client/applications?search=TKRU1234567&cf=…`, the page re-reads those params on the
  // way in and re-applies company А's filter to company Б's list. Clearing the storage
  // alone is not enough: on init the URL wins over storage, by design.
  if (search) {
    navigate(pathname, { replace: true, state: FORCED_LEAVE_STATE });
  }
}

// ============================================================================
// Applying the switch
// ============================================================================

export interface ApplyCompanySwitchParams {
  queryClient: QueryClient;
  /**
   * Already known — the switch mutation's own response. Omit when the caller only has a
   * bare `customerId` (the SignalR path): this then fetches `/users/me` itself.
   */
  profile?: ClientProfileDto;
}

/**
 * Drops the whole portal query cache, restores the fresh profile into it, keeps
 * `authStore`'s snapshot in step, and forces the messenger socket to re-announce itself.
 *
 * **Call {@link discardForeignRouteOnCompanySwitch} first, always** — see that function's
 * own doc for the race this avoids. This function does not navigate at all, on purpose:
 * every caller already knows its own `navigate`/`pathname`, and mixing "decide to
 * navigate" into an async data function is exactly how the ordering above gets lost the
 * next time someone edits either half.
 *
 * The profile is written back explicitly (`setQueryData`) rather than left to an implicit
 * observer-triggered refetch — a tab with no `useClientProfile()` currently mounted
 * anywhere would otherwise sit on an empty cache until something happens to query it, but
 * `authStore.user` (read by `ClientAccountBlock`, breadcrumbs, guards) needs the new
 * company right away.
 *
 * Never throws — a company switch that already committed server-side must not be reported
 * as failed because a cache write or a socket hiccuped; the profile refetch and the hub
 * reconnect both swallow their own failures.
 */
export async function applyCompanySwitch({
  queryClient,
  profile,
}: ApplyCompanySwitchParams): Promise<void> {
  queryClient.removeQueries({ queryKey: clientKeys.all });

  const resolved = profile ?? (await clientCompanyApi.getProfile().catch(() => null));

  if (resolved) {
    queryClient.setQueryData(clientKeys.profile(), resolved);
    useAuthStore.getState().updateUser({
      customerId: resolved.customerId,
      customerName: resolved.customerName,
    });
  }

  try {
    await restartClientMessengerHub();
  } catch {
    // Best-effort — useClientChatRealtime's own connection-state handling covers a socket
    // that stays down from here exactly like any other dropped connection would.
  }
}
